import type { ChangeEvent, FC } from 'react'
import * as Yup from 'yup'
import { FieldInputProps, useField, useFormikContext } from 'formik'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { TextField, TextFieldProps } from '@mui/material'
import { tss } from '@dhl/theme'

import { ErrorInputAdornment } from './ErrorInputAdornment.js'

type Props = Omit<TextFieldProps, keyof FieldInputProps<string>> & {
  maxLength?: number
  name: string
  autoSubmit?: boolean
}

const useStyles = tss.makeStyles()((theme) => ({
  invalidCode: {
    color: theme.palette.error.main,
  },
  validCode: {
    color: theme.palette.success.main,
  },
}))

export const VerificationCodeInput: FC<Props> = ({ maxLength = 6, name, autoSubmit = false, ...props }) => {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const [field, meta, helpers] = useField<string>(name)
  const { submitForm } = useFormikContext()

  const isGreen = field.value.length === maxLength && !meta.error
  const getTextClass = isGreen ? classes.validCode : classes.invalidCode

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim().replace(/[^0-9]+/gi, '') ?? ''

    helpers.setValue(value, true)

    if (autoSubmit) {
      setTimeout(submitForm, 0)
    }
  }

  return (
    <TextField
      {...props}
      {...field}
      label={t('inputs.verificationCodeInput.verificationCodeLabelText')}
      error={!!meta.error && meta.touched}
      helperText={
        meta.touched && meta.error ? meta.error : t('inputs.verificationCodeInput.verificationCodeHelperText')
      }
      onChange={onChange}
      variant="outlined"
      fullWidth
      data-test="verification-code-input"
      slotProps={{
        input: {
          classes: { input: getTextClass },
          endAdornment: <ErrorInputAdornment visible={meta.touched && !!meta.error} />,
        },

        htmlInput: { inputMode: 'numeric', maxLength, pattern: `[0-9]{${maxLength}}` },
      }}
    />
  )
}

export const createVerificationCodeValidationSchema = (
  t: TFunction,
  minLength = 6,
  maxLength = 6
): Yup.StringSchema<string> =>
  Yup.string()
    .required(t('inputs.verificationCodeInput.isMandatoryText'))
    .min(minLength, t('inputs.verificationCodeInput.isTooShortText', { min: minLength }))
    .max(maxLength, t('inputs.verificationCodeInput.isTooLongText', { max: maxLength }))
    .matches(/^[0-9]{6}$/, t('inputs.verificationCodeInput.shouldContainNumbers'))
